import React from "react";
import { Radio, Select, TextField } from "components";
import { useExemptionsContext } from "../../ExemptionsContext";
import { useEventTracker } from "utils/useEventTracker";
import constants from "utils/constants";
import { usePropertyContext } from "../../context";
import moment from "moment";
import { formatDollarStr } from "utils/formatDollar";
import ButtonV2 from "elements/ButtonV2";

const filingStatusOptions = [
	{ label: "Single", value: "Single" },
	{ label: "Married Filing Jointly", value: "Married filing jointly" },
	{ label: "Married Filing Separate", value: "Married filing separate" },
	{ label: "Head of Household", value: "Head of Household" },
	{
		label: "Qualifying Widower with Dependent Child",
		value: "Qualifying widower with dependent child",
	},
];

const TaxFilingHistoryDetailsShared = ({
	variant,
}: {
	variant:
		| "first-owner-federal-return"
		| "second-owner-return-type"
		| "first-owner-form-ny-return-last-five-years";
}) => {
	const {
		flowId,
		next,
		maritalStatusValue,
		additionalOwnerFirstNameValue,
		additionalOwnerLastNameValue,
		specialFieldsValue,
		ownersValue,
		setSpecialFieldsValue,
	} = useExemptionsContext();
	const { details } = usePropertyContext();
	const trackEvent = useEventTracker();

	const handleSubmit = async () => {
		if (flowId === "NEW_YORK_EXEMPTION") {
			if (variant === "first-owner-federal-return") {
				trackEvent({
					eventName:
						constants.EVENTS.Exemptions_Flow_Tax_Filing_History_Details_Answer,
					data: {
						"Is End-To-End Signup": details?.exemption?.is_end_to_end_signup
							? "Yes"
							: "No",
					},
				});
			}

			if (variant === "second-owner-return-type") {
				trackEvent({
					eventName:
						constants.EVENTS
							.Exemptions_Flow_Tax_Filing_History_Details_2_Answer,
					data: {
						"Is End-To-End Signup": details?.exemption?.is_end_to_end_signup
							? "Yes"
							: "No",
					},
				});
			}
		}
		const trackingEventData = {
			variant: variant,
			flow: flowId,
		};
		next({ trackingEventData });
	};

	const twoYearsAgo = React.useMemo(
		() => moment().subtract(2, "years").format("YYYY"),
		[]
	);

	const title = React.useMemo(() => {
		if (flowId === "NEW_YORK_EXEMPTION") {
			if (variant === "first-owner-federal-return") {
				return `What was your gross income and filing status in ${twoYearsAgo}?`;
			}
			if (variant === "second-owner-return-type") {
				return `What was ${additionalOwnerFirstNameValue} ${additionalOwnerLastNameValue} gross income and filing status in ${twoYearsAgo}?`;
			}
			if (variant === "first-owner-form-ny-return-last-five-years") {
				return "What was the income information and refund type you reported on the most recent IT-201 or IT-203 form?";
			}
		}

		return "";
	}, [
		flowId,
		variant,
		twoYearsAgo,
		additionalOwnerFirstNameValue,
		additionalOwnerLastNameValue,
	]);

	const subtitle = React.useMemo(() => {
		if (flowId === "NEW_YORK_EXEMPTION") {
			if (variant === "first-owner-federal-return") {
				return (
					<>
						This is the gross income and filing status you provided on your{" "}
						{twoYearsAgo} tax returns. Do not include other household members
						incomes. If you need help finding this information, you can always
						view Tax Return transcripts online at the IRS website.
					</>
				);
			}

			if (variant === "second-owner-return-type") {
				return (
					<>
						This is the gross income and filing status{" "}
						{additionalOwnerFirstNameValue} {additionalOwnerLastNameValue}{" "}
						provided on their {twoYearsAgo} tax returns. Do not include other
						household members incomes. If you need help finding this
						information, you can always view Tax Return transcripts online at
						the IRS website.
					</>
				);
			}

			if (variant === "first-owner-form-ny-return-last-five-years") {
				return (
					<>
						This is the information you provided on your most recent New York
						State Tax return. Do not include other household members incomes.{" "}
						This information must be accurate to avoid delays and rejection of
						your application.
					</>
				);
			}
		}

		return "";
	}, [
		flowId,
		variant,
		twoYearsAgo,
		additionalOwnerFirstNameValue,
		additionalOwnerLastNameValue,
	]);

	const taxFilingGrossIncomeValue = React.useMemo(() => {
		if (variant === "first-owner-federal-return") {
			return specialFieldsValue?.income_tax_history?.[0]?.owner1?.gross_income;
		}

		if (variant === "second-owner-return-type") {
			return specialFieldsValue?.income_tax_history?.[0]?.owner2?.gross_income;
		}

		if (variant === "first-owner-form-ny-return-last-five-years") {
			return specialFieldsValue?.most_recent_ny_state_tax_return?.gross_income;
		}
	}, [specialFieldsValue, variant]);

	const setTaxFilingGrossIncomeValue = (value: string) => {
		if (variant === "first-owner-federal-return") {
			setSpecialFieldsValue({
				income_tax_history: [
					{
						...specialFieldsValue?.income_tax_history?.[0],
						owner1: {
							...specialFieldsValue?.income_tax_history?.[0]?.owner1,
							gross_income: parseFloat(value.replace(/[^0-9.]/g, "")),
						},
					},
				],
			});
		}

		if (variant === "second-owner-return-type") {
			setSpecialFieldsValue({
				income_tax_history: [
					{
						...specialFieldsValue?.income_tax_history?.[0],
						owner2: {
							...specialFieldsValue?.income_tax_history?.[0]?.owner2,
							gross_income: parseFloat(value.replace(/[^0-9.]/g, "")),
						},
					},
				],
			});
		}

		if (variant === "first-owner-form-ny-return-last-five-years") {
			setSpecialFieldsValue({
				most_recent_ny_state_tax_return: {
					...specialFieldsValue?.most_recent_ny_state_tax_return,
					gross_income: parseFloat(value.replace(/[^0-9.]/g, "")),
				},
			});
		}
	};

	const taxFilingTaxableIncomeValue = React.useMemo(() => {
		if (variant === "first-owner-federal-return") {
			return specialFieldsValue?.income_tax_history?.[0]?.owner1
				?.taxable_income;
		}

		if (variant === "first-owner-form-ny-return-last-five-years") {
			return specialFieldsValue?.most_recent_ny_state_tax_return
				?.taxable_income;
		}
	}, [specialFieldsValue, variant]);

	const setTaxFilingTaxableIncomeValue = (value: string) => {
		if (variant === "first-owner-federal-return") {
			setSpecialFieldsValue({
				income_tax_history: [
					{
						...specialFieldsValue?.income_tax_history?.[0],
						owner1: {
							...specialFieldsValue?.income_tax_history?.[0]?.owner1,
							taxable_income: parseFloat(value.replace(/[^0-9.]/g, "")),
						},
					},
				],
			});
		}

		if (variant === "first-owner-form-ny-return-last-five-years") {
			setSpecialFieldsValue({
				most_recent_ny_state_tax_return: {
					...specialFieldsValue?.most_recent_ny_state_tax_return,
					taxable_income: parseFloat(value.replace(/[^0-9.]/g, "")),
				},
			});
		}
	};

	const taxFilingStatusValue = React.useMemo(() => {
		if (variant === "first-owner-federal-return") {
			return specialFieldsValue?.income_tax_history?.[0]?.owner1?.filing_status;
		}

		if (variant === "second-owner-return-type") {
			return specialFieldsValue?.income_tax_history?.[0]?.owner2?.filing_status;
		}

		if (variant === "first-owner-form-ny-return-last-five-years") {
			return specialFieldsValue?.most_recent_ny_state_tax_return?.filing_status;
		}
	}, [specialFieldsValue, variant]);

	const setTaxFilingStatusValue = (value: string) => {
		if (variant === "first-owner-federal-return") {
			setSpecialFieldsValue({
				income_tax_history: [
					{
						...specialFieldsValue?.income_tax_history?.[0],
						owner1: {
							...specialFieldsValue?.income_tax_history?.[0]?.owner1,
							filing_status: value,
						},
					},
				],
			});
		}

		if (variant === "second-owner-return-type") {
			setSpecialFieldsValue({
				income_tax_history: [
					{
						...specialFieldsValue?.income_tax_history?.[0],
						owner2: {
							...specialFieldsValue?.income_tax_history?.[0]?.owner2,
							filing_status: value,
						},
					},
				],
			});
		}

		if (variant === "first-owner-form-ny-return-last-five-years") {
			setSpecialFieldsValue({
				most_recent_ny_state_tax_return: {
					...specialFieldsValue?.most_recent_ny_state_tax_return,
					filing_status: value,
				},
			});
		}
	};

	const taxFilingRefundValue = React.useMemo(() => {
		if (variant === "first-owner-federal-return") {
			return specialFieldsValue?.income_tax_history?.[0]?.owner1
				?.received_refund;
		}

		if (variant === "first-owner-form-ny-return-last-five-years") {
			return specialFieldsValue?.most_recent_ny_state_tax_return
				?.requested_refund;
		}
	}, [specialFieldsValue, variant]);

	const setTaxFilingRefundValue = (value: boolean) => {
		if (variant === "first-owner-federal-return") {
			setSpecialFieldsValue({
				income_tax_history: [
					{
						...specialFieldsValue?.income_tax_history?.[0],
						owner1: {
							...specialFieldsValue?.income_tax_history?.[0]?.owner1,
							received_refund: value,
						},
					},
				],
			});
		}

		if (variant === "first-owner-form-ny-return-last-five-years") {
			setSpecialFieldsValue({
				most_recent_ny_state_tax_return: {
					...specialFieldsValue?.most_recent_ny_state_tax_return,
					requested_refund: value,
				},
			});
		}
	};

	const taxFilingRefundAmountValue = React.useMemo(() => {
		if (variant === "first-owner-federal-return") {
			return specialFieldsValue?.income_tax_history?.[0]?.owner1?.refund_amount;
		}

		if (variant === "first-owner-form-ny-return-last-five-years") {
			return taxFilingRefundValue
				? specialFieldsValue?.most_recent_ny_state_tax_return
						?.refund_amount_requested
				: specialFieldsValue?.most_recent_ny_state_tax_return
						?.refund_amount_owed;
		}
	}, [specialFieldsValue, variant]);

	const setTaxFilingRefundAmountValue = (value: string) => {
		if (variant === "first-owner-federal-return") {
			setSpecialFieldsValue({
				income_tax_history: [
					{
						...specialFieldsValue?.income_tax_history?.[0],
						owner1: {
							...specialFieldsValue?.income_tax_history?.[0]?.owner1,
							refund_amount: parseFloat(value.replace(/[^0-9.]/g, "")),
						},
					},
				],
			});
		}

		if (variant === "first-owner-form-ny-return-last-five-years") {
			setSpecialFieldsValue({
				most_recent_ny_state_tax_return: {
					...specialFieldsValue?.most_recent_ny_state_tax_return,
					...(taxFilingRefundValue
						? {
								refund_amount_requested: parseFloat(
									value.replace(/[^0-9.]/g, "")
								),
							}
						: {
								refund_amount_owed: parseFloat(value.replace(/[^0-9.]/g, "")),
							}),
				},
			});
		}
	};

	const buttonDisabled = React.useMemo(() => {
		if (variant === "first-owner-federal-return") {
			return (
				!specialFieldsValue?.income_tax_history?.[0]?.owner1?.filing_status ||
				!specialFieldsValue?.income_tax_history?.[0]?.owner1?.gross_income ||
				!specialFieldsValue?.income_tax_history?.[0]?.owner1?.taxable_income ||
				(!!specialFieldsValue?.income_tax_history?.[0]?.owner1
					?.received_refund &&
					!specialFieldsValue?.income_tax_history?.[0]?.owner1?.refund_amount)
			);
		}
		if (variant === "second-owner-return-type") {
			return (
				!specialFieldsValue?.income_tax_history?.[0]?.owner2?.filing_status ||
				!specialFieldsValue?.income_tax_history?.[0]?.owner2?.gross_income
			);
		}
		if (variant === "first-owner-form-ny-return-last-five-years") {
			const { requested_refund, refund_amount_requested, refund_amount_owed } =
				specialFieldsValue?.most_recent_ny_state_tax_return || {};
			if (requested_refund === undefined) return true;
			if (
				requested_refund == true &&
				(refund_amount_requested === undefined ||
					isNaN(refund_amount_requested))
			)
				return true;
			if (
				requested_refund == false &&
				(refund_amount_owed === undefined || isNaN(refund_amount_owed))
			)
				return true;
			return (
				!specialFieldsValue?.most_recent_ny_state_tax_return?.filing_status ||
				!specialFieldsValue?.most_recent_ny_state_tax_return?.gross_income ||
				!specialFieldsValue?.most_recent_ny_state_tax_return?.taxable_income ||
				!specialFieldsValue?.most_recent_ny_state_tax_return?.tax_year ||
				!specialFieldsValue?.most_recent_ny_state_tax_return
					?.total_payments_amount
			);
		}
	}, [specialFieldsValue, variant]);

	const options = React.useMemo(() => {
		if (flowId === "NEW_YORK_EXEMPTION") {
			if (
				(ownersValue === "Own With Spouse" &&
					specialFieldsValue?.income_tax_history?.[0]?.owner1?.filing_status ===
						"Married filing jointly") ||
				(maritalStatusValue === "Married" &&
					specialFieldsValue?.income_tax_history?.[0]?.owner1?.filing_status ===
						"Married filing jointly")
			) {
				return filingStatusOptions.filter(
					option => option.value === "Married filing jointly"
				);
			}
		}

		return filingStatusOptions;
	}, [flowId, specialFieldsValue, ownersValue]);

	return (
		<form
			onSubmit={e => {
				e.preventDefault();
				handleSubmit();
			}}>
			<div className="property-profile-question-card exemption-survey-card">
				<div className="property-profile-question-card-title-container">
					<p>{title}</p>
				</div>
				<p className="exemption-question-card-subtitle sm">{subtitle}</p>
				<Select
					containerClassName="exemptions-card-input"
					label="Filing Status"
					required
					value={taxFilingStatusValue ?? ""}
					onChange={e => setTaxFilingStatusValue(e.target.value)}>
					<option value="" hidden disabled>
						Filing Status
					</option>
					{options.map(({ label, value }) => (
						<option key={label} value={value}>
							{label}
						</option>
					))}
				</Select>
				{variant === "first-owner-form-ny-return-last-five-years" && (
					<Select
						containerClassName="exemptions-card-input"
						label="Tax Year"
						required
						value={
							specialFieldsValue?.most_recent_ny_state_tax_return?.tax_year ||
							""
						}
						onChange={e => {
							setSpecialFieldsValue({
								most_recent_ny_state_tax_return: {
									...specialFieldsValue?.most_recent_ny_state_tax_return,
									tax_year: Number(e.target.value),
								},
							});
						}}>
						<option value="" hidden disabled>
							Tax Year
						</option>
						{Array(5)
							.fill(0)
							.map((_, i) => moment().year() - (i + 1))
							.sort()
							.map(year => (
								<option key={year} value={year}>
									{year}
								</option>
							))}
					</Select>
				)}
				<TextField
					required
					label="Gross Income"
					placeholder="$"
					containerClassName="exemptions-card-input"
					autoComplete="off"
					value={formatDollarStr(taxFilingGrossIncomeValue || "")}
					onChange={e => {
						setTaxFilingGrossIncomeValue(formatDollarStr(e.target.value) || "");
					}}
				/>
				{variant !== "second-owner-return-type" && (
					<>
						<TextField
							required
							label="Taxable Income"
							placeholder="$"
							containerClassName="exemptions-card-input"
							autoComplete="off"
							value={formatDollarStr(taxFilingTaxableIncomeValue || "")}
							onChange={e => {
								setTaxFilingTaxableIncomeValue(
									formatDollarStr(e.target.value) || ""
								);
							}}
						/>
						{variant === "first-owner-form-ny-return-last-five-years" && (
							<>
								<TextField
									required
									label={`${moment().year() - 2} Total Payments Amount`}
									placeholder="$"
									containerClassName="exemptions-card-input"
									autoComplete="off"
									value={formatDollarStr(
										specialFieldsValue?.most_recent_ny_state_tax_return
											?.total_payments_amount || ""
									)}
									onChange={e => {
										setSpecialFieldsValue({
											most_recent_ny_state_tax_return: {
												...specialFieldsValue?.most_recent_ny_state_tax_return,
												total_payments_amount: parseFloat(
													e.target.value.replace(/[^0-9.]/g, "")
												),
											},
										});
									}}
								/>
								<p className="sm denim_5 mt-1">
									Your {moment().year() - 2} Total Payments Amount is located on
									your IT-201 lines 76 or 77, or IT-203 lines 66 or 67.
								</p>
							</>
						)}
						<div className="property-profile-question-card-title-container mt-2">
							<p>
								{variant === "first-owner-federal-return"
									? "Did you receive a refund?"
									: "Did you request a refund?"}
							</p>
							<div className="property-profile-question-card-radio">
								<Radio
									checked={taxFilingRefundValue === true}
									onChange={e => {
										//@ts-ignore
										const value = e.target.value === "Yes";
										setTaxFilingRefundValue(value);
									}}
									value="Yes"
									label="Yes"
								/>
								<Radio
									checked={taxFilingRefundValue === false}
									onChange={e => {
										//@ts-ignore
										const value = e.target.value === "Yes";
										setTaxFilingRefundValue(value);
									}}
									value="No"
									label="No"
								/>
							</div>
						</div>
						{((variant === "first-owner-federal-return" &&
							!!taxFilingRefundValue) ||
							(variant === "first-owner-form-ny-return-last-five-years" &&
								taxFilingRefundValue !== undefined)) && (
							<TextField
								required
								label={
									variant === "first-owner-federal-return"
										? "Refund Amount"
										: taxFilingRefundValue
											? "Amount Requested"
											: "Amount You Are Owed"
								}
								placeholder="$"
								containerClassName="exemptions-card-input"
								autoComplete="off"
								value={
									taxFilingRefundAmountValue === 0
										? "$0"
										: formatDollarStr(taxFilingRefundAmountValue || "")
								}
								onChange={e => {
									setTaxFilingRefundAmountValue(
										formatDollarStr(e.target.value) || ""
									);
								}}
							/>
						)}
					</>
				)}
			</div>
			<ButtonV2 size="large" disabled={buttonDisabled} type="submit">
				Continue
			</ButtonV2>
		</form>
	);
};

export const TaxFilingHistoryDetails = () => (
	<TaxFilingHistoryDetailsShared variant="first-owner-federal-return" />
);
export const TaxFilingHistoryDetails2 = () => (
	<TaxFilingHistoryDetailsShared variant="second-owner-return-type" />
);
export const TaxFilingHistoryDetailsNYLastFiveYears = () => (
	<TaxFilingHistoryDetailsShared variant="first-owner-form-ny-return-last-five-years" />
);
